/**
 * Every TanStack Query key in the SPA, and the only place one is written.
 *
 * `.docs/00-index.md` §5: "Query keys → `apps/web/src/api/keys.ts`. One factory per
 * module." `ARCHITECTURE.md` §Frontend: "Query keys are `[module, entity, id |
 * params]`; mutations invalidate by module."
 *
 * The shape is not decoration. A mutation says which module it touched and
 * `invalidateModule` invalidates the prefix, so a component never has to know which
 * of nine queries a write disturbed — which is the failure that produces a page
 * showing last minute's numbers with no way to tell.
 *
 * **To add a module:** a row in `API_MODULES`, then its entities on `queryKeys`.
 * The union is derived from the array, so the two cannot disagree and
 * `keys.test.ts` walks every factory to check the first element is its own module.
 *
 * **A parameter that is optional is spread, not defaulted.** `invoices()` and
 * `invoices('paid')` are two cache entries and `invoices(undefined)` is the first
 * one — an `undefined` left in the array would make it a third that nothing else
 * ever produces. `param()` below is that spread, once.
 *
 * The studio's reads are the one thing not in `queryKeys`, and §The studio's own
 * reads at the bottom says why: they are addressed by action name, so they are one
 * function (`studioKey`) over one table (`moduleOfAction`) rather than 158 entries.
 */

import { domainOfAction, type ActionDomain } from '@fareast/shared'

/**
 * The modules the SPA reads. Sessions 11 onwards append; the list is here rather
 * than in each session's own file so "what can be invalidated" is one screen.
 *
 * Session 19 adds the eight the client portal reads and writes. `tasks` and
 * `analytics` are session 20's and are not here — a module with no key is a module
 * `keys.unit.test.ts` cannot check, and an unused union member is a promise rather
 * than a fact.
 */
export const API_MODULES = [
  'session',
  'clients',
  'core',
  'files',
  'studio',
  'catalog',
  'work-orders',
  'retainers',
  'usage',
  'billing',
  'calls',
  'reports',
  // Session 20's two. `tasks` is the board and the detail route; `analytics` is the
  // ten `analytics_*` reads, which no module's writes invalidate — a retainer
  // starting moves MRR, so `retainers`' writes name it in `alsoInvalidates`.
  'tasks',
  'analytics',
  'expenses',
  /*
   * Session 46's blog workspace (`pages/blog/`), its own module rather than a guest in
   * `usage`: an autosave lands every few seconds while somebody writes, and under
   * `usage` each one re-read the widget pages and the balance for nothing. A publish —
   * the one write here that meters — names `usage` itself (`alsoInvalidates`).
   */
  'blog',
  /*
   * Session 46's forms workspace (`pages/forms/`), its own module for the blog's reason:
   * opening a lead marks it read, and under `usage` that one timestamp re-read every
   * widget page, the balance and the sidebar. "Not spam" — the one press that can meter
   * — names `usage` itself (`api/client/forms.ts`).
   */
  'forms',
  /*
   * Session 48's mockups (`pages/mockups/`): the client's list and viewer and the studio's
   * section and viewer, over one module so a comment from either door re-reads both.
   */
  'mockups',
] as const

export type ApiModule = (typeof API_MODULES)[number]

/** The prefix every key in a module starts with. What a mutation invalidates. */
export function moduleKey(module: ApiModule): readonly [ApiModule] {
  return [module]
}

/**
 * An optional key segment, spread rather than defaulted.
 *
 * `[...param(period)]` is `[]` for `undefined` and `['2026-08']` otherwise, so the
 * page that asks for "this month" and the page that asks for August share a cache
 * entry only when they mean the same thing.
 */
function param(value: string | undefined): readonly string[] {
  return value === undefined ? [] : [value]
}

export const queryKeys = {
  /**
   * The signed-in person, as `auth/session.ts` derives them. One entry, read by
   * the router's guards and by the shell, so the two cannot disagree.
   */
  session: {
    principal: () => ['session', 'principal'] as const,
    /**
     * `GET /api/auth-methods` — which identity providers this deployment offers.
     *
     * Under `session` because it is a fact about signing in rather than about a
     * client, and because it is read on `/login`, where there is no client yet.
     * Nothing invalidates it: it changes when somebody edits the box's `.env` and
     * restarts, which ends this document.
     */
    authMethods: () => ['session', 'auth-methods'] as const,
    /**
     * `GET /api/auth/oauth2/public-client` — what an application asking for an OAuth
     * grant calls itself, read by `/oauth/consent` and nowhere else.
     *
     * Under `session` for the same reason `authMethods` is: it is a fact about
     * signing something in, on a screen that belongs to no client. Nothing
     * invalidates it — a registration is written once and the screen it feeds ends
     * in a full document load off this origin.
     */
    oauthClient: (clientId: string) => ['session', 'oauth-client', clientId] as const,
  },

  clients: {
    /** `GET /api/clients/me` — the account. The `sidebar__org` line. */
    me: () => ['clients', 'me'] as const,
    /**
     * `GET /api/clients/invitations/:id` — the offer an invited person is shown
     * before they hold a seat anywhere.
     *
     * The one key in this module that is **not** about "the client this request is
     * scoped to": somebody reading it is scoped to nothing, and the id in the URL is
     * the whole of what identifies it (`modules/clients/routes.ts`: "Not under `me`
     * … somebody who has not accepted yet is scoped to nothing"). It is still keyed
     * by `clients`, because accepting is a `clients` write and that is what makes
     * `useAcceptInvitation` drop this entry without naming it.
     */
    invitation: (id: string) => ['clients', 'invitation', id] as const,
    /** `GET /api/clients/me/members` — the People card (session 19). */
    members: () => ['clients', 'members'] as const,
    /** `GET /api/clients/me/notification-prefs` — the switches (session 19). */
    notificationPrefs: () => ['clients', 'notification-prefs'] as const,
    /** `GET /api/clients/me/credentials` — labels only; the reveal is a mutation. */
    credentials: () => ['clients', 'credentials'] as const,
    /**
     * The widgets this client has, which become nav items (session 13). The route
     * is `usage`'s and the key is `clients`' because the **nav** is what reads it:
     * a widget's cap changing must repaint the sidebar, and `usage`'s own writes
     * name `clients` in `alsoInvalidates` for that reason.
     */
    widgets: () => ['clients', 'widgets'] as const,
  },

  catalog: {
    /** `GET /api/catalog` — `client/billing.html` §3–§4, what else we do. */
    all: () => ['catalog', 'all'] as const,
  },

  /**
   * The three cross-module reads `modules/core/routes.ts` owns: the whole
   * dashboard, the merged decisions list and the upcoming timeline.
   *
   * They are their own module rather than a guest in `work-orders` because no one
   * module's writes cover them — a package change, a content-plan sign-off and a
   * quote approval each move the same list. Every write that decides something
   * therefore names `core` in `alsoInvalidates`, which is one word at each call
   * site and the alternative to three pages disagreeing about how many approvals
   * are waiting.
   */
  core: {
    /** `GET /api/clients/me/dashboard` — metrics, allowances, decisions, upcoming. */
    dashboard: () => ['core', 'dashboard'] as const,
    /** `GET /api/clients/me/dashboard/decisions` — the merged four kinds. */
    decisions: () => ['core', 'decisions'] as const,
    /** `GET /api/clients/me/dashboard/upcoming` — the timeline on its own. */
    upcoming: () => ['core', 'upcoming'] as const,
  },

  /**
   * The client's own reads of the studio's board. Session 43's `/app/work`, and for
   * now the only entry — the studio's task reads are addressed by action name and go
   * through `studioKey`/`studioPagesKey`, which already start with this module.
   *
   * Under `tasks` rather than `core` even though `modules/core/routes.ts` answers it,
   * because invalidation is about what a write disturbs and the writes here are
   * `tasks_*`: a studio member completing a task has to move this page, and
   * `moduleOfAction('tasks_complete')` is already `tasks`. A key under `core` would
   * have needed every task action to name `core` in `alsoInvalidates` to say the same
   * thing less directly.
   */
  tasks: {
    /** `GET /api/clients/me/work` — everything in flight, a page at a time. */
    work: () => ['tasks', 'work'] as const,
  },

  files: {
    byId: (id: string) => ['files', 'by-id', id] as const,
    /** `GET /api/clients/me/files?period=` — all three tabs of `files.html`. */
    page: (period?: string) => ['files', 'page', ...param(period)] as const,
  },

  'work-orders': {
    /** `GET /api/clients/me/requests` — the ledger, by window and kind. */
    requests: (window?: string) => ['work-orders', 'requests', ...param(window)] as const,
    /**
     * One request, by reference — `GET /api/clients/me/requests/:ref`.
     *
     * **Separate from `projectByRef` on purpose.** The two routes answer different
     * shapes for the same `WO-` reference (a request's own detail, and a project's
     * detail with its milestone schedule), and a single key served whichever landed
     * first to both callers. Nothing renders both for one reference today, which is
     * exactly why it would have been found late.
     */
    byRef: (ref: string) => ['work-orders', 'request', ref] as const,
    /** One project, by reference — `GET /api/clients/me/projects/:ref`. */
    projectByRef: (ref: string) => ['work-orders', 'project', ref] as const,
    /** `GET /api/clients/me/projects`. */
    projects: () => ['work-orders', 'projects'] as const,
  },

  retainers: {
    /** `GET /api/clients/me/retainer` — the package and its blocks. */
    retainer: () => ['retainers', 'retainer'] as const,
    /** `GET /api/clients/me/retainer/allowances?period=`. */
    allowances: (period?: string) => ['retainers', 'allowances', ...param(period)] as const,
    /** `GET /api/clients/me/retainer/changes` — `RET-CHG-NNN`. */
    changes: () => ['retainers', 'changes'] as const,
    /** `GET /api/clients/me/content-plans`. */
    contentPlans: () => ['retainers', 'content-plans'] as const,
    /** `GET /api/clients/me/deliverables?period=`. */
    deliverables: (period?: string) => ['retainers', 'deliverables', ...param(period)] as const,
  },

  usage: {
    /** `GET /api/clients/me/widgets?period=` — the list plus this period's totals. */
    widgets: (period?: string) => ['usage', 'widgets', ...param(period)] as const,
    /** `GET /api/clients/me/widgets/:ref?period=` — one widget's page. */
    widget: (ref: string, period?: string) => ['usage', 'widget', ref, ...param(period)] as const,
    /** `GET /api/clients/me/credits` — the prepaid balance and the standing top-up. */
    credits: () => ['usage', 'credits'] as const,
    /**
     * `GET /api/clients/me/widgets/:ref/hooks` — a hosted widget's site hooks (session
     * 46, the Connect page). Under `usage` because a hook hangs off a widget, and
     * because the studio's `hooks_*` reads are keyed there too (`moduleOfAction`), so a
     * write from either door re-reads both.
     */
    siteHooks: (widget: string) => ['usage', 'site-hooks', widget] as const,
    /**
     * `GET …/hooks/deliveries?hook=&status=` — the delivery log, a page at a time. The
     * filter is one object segment, for `queryKeys.blog.posts`' reason: two optional
     * positional segments would key a status with no hook like a hook with no status.
     */
    siteHookDeliveries: (
      widget: string,
      filter: { readonly hook?: string; readonly status?: string } = {},
    ) => ['usage', 'site-hook-deliveries', widget, filter] as const,
    /** `GET …/hooks/deliveries?hook=&limit=1` — one hook's newest delivery, for its row. */
    siteHookLast: (widget: string, hook: string) =>
      ['usage', 'site-hook-last', widget, hook] as const,
  },

  billing: {
    /** `GET /api/clients/me/invoices` — the ledger on `billing.html` §7. */
    invoices: () => ['billing', 'invoices'] as const,
    /** One invoice, with its lines and its payments. */
    invoice: (ref: string) => ['billing', 'invoice', ref] as const,
    /** `GET /api/clients/me/invoices/next-estimate` — "the next one is …". */
    nextEstimate: () => ['billing', 'next-estimate'] as const,
    /** `GET /api/clients/me/payment-method` — the masked card or mandate. */
    paymentMethod: () => ['billing', 'payment-method'] as const,
  },

  calls: {
    /** `GET /api/clients/me/calls` — next, upcoming and past. */
    page: () => ['calls', 'page'] as const,
    /** `GET /api/clients/me/calls/rules` — the fold and the four types. */
    rules: () => ['calls', 'rules'] as const,
    /** `GET /api/clients/me/calls/availability?week=&type=` — the grid. */
    availability: (week?: string, type?: string) =>
      ['calls', 'availability', ...param(week), ...param(type)] as const,
  },

  reports: {
    /** `GET /api/clients/me/reports` — the archive. */
    archive: () => ['reports', 'archive'] as const,
    /** `GET /api/clients/me/reports/:period` — one month's document. */
    document: (period: string) => ['reports', 'document', period] as const,
    /** `GET /api/clients/me/metrics?period=` — the dashboard's four figures. */
    metrics: (period?: string) => ['reports', 'metrics', ...param(period)] as const,
  },

  studio: {
    /** `GET /api/studio/_catalogue` — what session 20's action forms are built from. */
    catalogue: () => ['studio', 'catalogue'] as const,
  },

  /**
   * A hosted blog's workspace, as the client's REST door answers it —
   * `GET /api/clients/me/widgets/:ref/blog/…` (session 46, `api/client/blog.ts`). The
   * studio's half reads the `blog_*` actions, keyed by `studioKey` under this same
   * module, so a write from either door re-reads both.
   *
   * Every key carries the widget: two blogs on one account are two caches.
   */
  blog: {
    /**
     * The posts list, a page at a time. The filter is one object segment rather than
     * two spread ones, because two optional positional segments collide — a search with
     * no phase would key exactly like a phase with no search. An absent field drops out
     * of the hash, so `{}` and `{ phase: undefined }` are one entry.
     */
    posts: (widget: string, filter: { readonly phase?: string; readonly q?: string } = {}) =>
      ['blog', 'posts', widget, filter] as const,
    /** Every filter of the posts list at once — what a draft save marks as behind. */
    postLists: (widget: string) => ['blog', 'posts', widget] as const,
    /** One post as the editor reads it: the working copy, what is live, the checks. */
    post: (widget: string, post: string) => ['blog', 'post', widget, post] as const,
    /** A post's history. */
    revisions: (widget: string, post: string) => ['blog', 'revisions', widget, post] as const,
    /** One revision, whole — by its number, as the path writes it. */
    revision: (widget: string, post: string, number: string) =>
      ['blog', 'revision', widget, post, number] as const,
    /** The blog's author directory. */
    authors: (widget: string) => ['blog', 'authors', widget] as const,
    /** The blog's images, newest first. */
    media: (widget: string) => ['blog', 'media', widget] as const,
  },

  /**
   * A forms widget's workspace — `GET /api/clients/me/widgets/:ref/forms…` and
   * `…/submissions…` (session 46, `api/client/forms.ts`). Every key carries the widget,
   * for the blog's reason.
   */
  forms: {
    /** Every form on the widget. */
    list: (widget: string) => ['forms', 'list', widget] as const,
    /** One form, by its key, as its editor opens it. */
    form: (widget: string, key: string) => ['forms', 'form', widget, key] as const,
    /**
     * The inbox, a page at a time. One object segment for the whole question, for the
     * blog's `posts` reason: four optional filters spread positionally would collide.
     */
    submissions: (
      widget: string,
      filter: {
        readonly form?: string | undefined
        readonly verdict?: string | undefined
        readonly state?: string | undefined
        readonly q?: string | undefined
      } = {},
    ) => ['forms', 'submissions', widget, filter] as const,
    /** One lead, with its delivery log. */
    submission: (widget: string, receipt: string) =>
      ['forms', 'submission', widget, receipt] as const,
  },

  mockups: {
    /** `GET /api/clients/me/mockups` — the client's list and the nav badge's count. */
    page: () => ['mockups', 'page'] as const,
    /** `GET /api/clients/me/mockups/:ref` — one mockup, every version and comment. */
    byRef: (ref: string) => ['mockups', 'by-ref', ref] as const,
    /**
     * `GET /api/mockups/link/:publicId` — the link's own viewer (session 49). Under `mockups`,
     * so a member's comment made from it, which invalidates the module, refreshes it too.
     */
    link: (publicId: string) => ['mockups', 'link', publicId] as const,
  },
} as const

/* --------------------------------------------------- the studio's own reads */

/**
 * The key for one studio read — `POST /api/studio/<action>` with an input.
 *
 * A function rather than a `queryKeys` entry per action, because there are 158
 * actions and they are addressed by **name**, not by a hand-written path: a factory
 * per read would be 60 lines that say what `moduleOfAction` already says.
 *
 * The first element is still the module, which is the whole rule — `invoices_charge$`
 * invalidates `billing` and the money page's `billing_summary` read is under
 * `['billing', …]`, so it refetches without anybody listing it. `keys.unit.test.ts`
 * asserts that, and asserts two different inputs are two entries.
 *
 * The input is serialised with its keys sorted, so `{ client, period }` and
 * `{ period, client }` are one cache entry rather than two. `undefined` fields drop
 * out, which is what makes `clients_summary({ status: undefined })` and
 * `clients_summary({})` the same read.
 */
export function studioKey(action: string, input?: Record<string, unknown>): readonly unknown[] {
  return [moduleOfAction(action), 'action', action, stableJson(input ?? {})] as const
}

/**
 * The key for one studio read **walked page by page** (`api/studio.ts` `useStudioPages`).
 *
 * `'pages'` where `studioKey` has `'action'`: TanStack keeps an infinite query's pages
 * in a different shape from a plain query's answer, so the same action and input
 * read both ways must be two cache entries, never one. The module comes first for
 * the same reason as `studioKey`'s, so a write refetches every page already loaded.
 * The input is the whole question, cursor excluded: a filter, a search or a sort
 * that changes is a new key and starts again from the first page.
 */
export function studioPagesKey(
  action: string,
  input?: Record<string, unknown>,
): readonly unknown[] {
  return [moduleOfAction(action), 'pages', action, stableJson(input ?? {})] as const
}

/**
 * Every page-by-page read of one action, whatever its input — the prefix of each
 * `studioPagesKey(action, …)`, for marking all of them at once.
 */
export function studioPagesOf(action: string): readonly unknown[] {
  return [moduleOfAction(action), 'pages', action] as const
}

/** `{ b: 1, a: undefined }` → `{"b":1}`. Sorted, and without the absent fields. */
function stableJson(input: Record<string, unknown>): string {
  const entries = Object.entries(input)
    .filter(([, value]) => value !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
  return JSON.stringify(Object.fromEntries(entries))
}

/**
 * Which module's queries a studio action's answer lives under, and therefore which
 * ones a write to it invalidates.
 *
 * Written out rather than derived, and every row is a decision:
 *
 *   - **`deliverables` and `content_plans` are `retainers`.** Both are things that
 *     happen inside a retainer's allowance, and the allowance is what a page shows.
 *   - **`credits` and `widgets` are `usage`.** A top-up moves the balance the usage
 *     page prints; a widget's cap moves what its month bills.
 *   - **`invoices` and `payments` are `billing`.** One ledger, and `billing_summary`
 *     is the read that shows all three.
 *   - **`agents`, `audit`, `notifications` and `settings` are `studio`.** None has a
 *     page of its own yet; `studio` is where a read with no module goes, beside the
 *     catalogue.
 *
 * `domainOfAction` is `packages/shared`'s — the same longest-prefix match
 * `apps/api/src/actions/vocab.ts` `splitActionName` uses, so `content_plans_draft` is
 * `content_plans` here and there rather than `content` in one of them. A name that
 * is not an action at all falls back to `studio`, which invalidates the least.
 */
export function moduleOfAction(action: string): ApiModule {
  const domain = domainOfAction(action)
  return domain === null ? 'studio' : DOMAIN_MODULES[domain]
}

const DOMAIN_MODULES = {
  agents: 'studio',
  clients: 'clients',
  catalog: 'catalog',
  retainers: 'retainers',
  deliverables: 'retainers',
  content_plans: 'retainers',
  work_orders: 'work-orders',
  widgets: 'usage',
  usage: 'usage',
  credits: 'usage',
  invoices: 'billing',
  payments: 'billing',
  billing: 'billing',
  files: 'files',
  calls: 'calls',
  reports: 'reports',
  tasks: 'tasks',
  notifications: 'studio',
  analytics: 'analytics',
  expenses: 'expenses',
  audit: 'studio',
  settings: 'studio',
  // Session 46: a site hook hangs off a hosted widget, and the widget page is `usage`'s.
  hooks: 'usage',
  // Session 46: a blog's posts, under the workspace's own module — what the client's
  // REST reads are keyed by too (`queryKeys.blog`), so either door's write re-reads both.
  blog: 'blog',
  // Session 46: a forms widget's forms and leads, under the workspace's own module — what
  // the client's REST reads are keyed by (`queryKeys.forms`), so a `forms_*` write over
  // the studio door re-reads them too.
  forms: 'forms',
  // Session 48: the studio's mockup tools and the client's mockup routes, one module.
  mockups: 'mockups',
} as const satisfies Readonly<Record<ActionDomain, ApiModule>>

/**
 * The modules a studio write disturbs **besides its own**, by domain.
 *
 * Every studio page is a cross-module composition, so "invalidate by module" alone
 * leaves a page stale in exactly the cases somebody would notice: start a retainer
 * and `/studio`'s MRR chart, the clients book and the money page all move, and none
 * of them is a `retainers` query.
 *
 * The rule is one line per reason, not one per action:
 *
 *   - **`analytics`** for anything that moves money or work — every one of those is a
 *     figure on `/studio`, which reads ten `analytics_*` actions and no others.
 *   - **`clients`** for anything the book prints per row: MRR, allowance burn, what is
 *     overdue, and the "waiting on a decision" list (which is tasks).
 *   - **`core`** for a decision, because the client portal's own merged decisions
 *     list is the same set — a studio member recording an offline approval has to
 *     move it there too, and session 19's pages all key off `core`.
 */
const DOMAIN_ALSO_INVALIDATES = {
  retainers: ['analytics', 'clients', 'core'],
  deliverables: ['analytics', 'clients'],
  content_plans: ['clients', 'core'],
  work_orders: ['analytics', 'clients', 'core'],
  invoices: ['analytics', 'clients'],
  payments: ['analytics', 'clients'],
  billing: ['analytics', 'clients'],
  usage: ['analytics', 'clients'],
  credits: ['analytics'],
  widgets: ['analytics', 'clients'],
  expenses: ['analytics'],
  tasks: ['clients'],
  clients: ['analytics'],
  catalog: ['analytics'],
  // A mockup tool can raise or touch a task (a comment's), and the board is `tasks`.
  mockups: ['tasks'],
} as const satisfies Partial<Readonly<Record<ActionDomain, readonly ApiModule[]>>>

/** What `useStudioAction` passes as `alsoInvalidates`. Empty for a domain with none. */
export function alsoInvalidatedBy(action: string): readonly ApiModule[] {
  const domain = domainOfAction(action)
  if (domain === null) return []
  return (DOMAIN_ALSO_INVALIDATES as Partial<Record<string, readonly ApiModule[]>>)[domain] ?? []
}
